import { useQuery } from '@tanstack/react-query'
import type {
  IQuickInstrumentListItem,
  IQuickInstrumentListResult,
} from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import { queryKeys } from '@/api/query-keys'
import { useSessionStore } from '@/stores/useSessionStore'
import type { DirectInstrumentStatusFilter } from '../lib/quick-instrument-display'
import { mapUiFilterToApiStatus, resolveBudgetUsedUsd } from '../lib/quick-instrument-display'

function tenantKey(tenantId: string | null | undefined) {
  return tenantId ?? '_'
}

export type QuickInstrumentsQueryParams = {
  status?: DirectInstrumentStatusFilter
  search?: string
  page?: number
  pageSize?: number
}

export type QuickInstrumentsResult = {
  items: IQuickInstrumentListItem[]
  total: number
  page: number
  pageSize: number
}

export async function fetchQuickInstruments(
  params: QuickInstrumentsQueryParams,
  signal?: AbortSignal,
): Promise<QuickInstrumentsResult> {
  const apiStatus = mapUiFilterToApiStatus(params.status ?? 'all')
  const search = params.search?.trim()
  const page = params.page ?? 1
  const pageSize = params.pageSize ?? 6

  const { data: envelope } = await apiClient.get<
    ApiResponse<IQuickInstrumentListResult | IQuickInstrumentListItem[]>
  >('/api/v1/instruments', {
    signal,
    params: {
      origin: 'quick',
      ...(apiStatus ? { status: apiStatus } : {}),
      ...(search ? { search } : {}),
      page,
      pageSize,
    },
  })

  const raw = envelope?.data
  if (Array.isArray(raw)) {
    return { items: raw.map(mapQuickListItem), total: raw.length, page, pageSize }
  }
  if (raw && typeof raw === 'object' && Array.isArray(raw.data)) {
    return {
      items: raw.data.map(mapQuickListItem),
      total: raw.total ?? raw.data.length,
      page: raw.page ?? page,
      pageSize: raw.pageSize ?? pageSize,
    }
  }
  return { items: [], total: 0, page, pageSize }
}

function mapQuickListItem(item: IQuickInstrumentListItem): IQuickInstrumentListItem {
  return {
    ...item,
    responseCount: item.responseCount ?? 0,
    budgetUsedUsd: resolveBudgetUsedUsd(item),
  }
}

export function useQuickInstruments(params: QuickInstrumentsQueryParams) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)
  const filters = {
    origin: 'quick' as const,
    status: params.status ?? 'all',
    search: params.search?.trim() ?? '',
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 6,
  }

  return useQuery({
    queryKey: queryKeys.instruments.list(tenantKey(tenantId), filters),
    queryFn: ({ signal }) => fetchQuickInstruments(params, signal),
    enabled: isAuthenticated,
  })
}
